import { money } from './format.js';

// The page people already know.
//
// This is the one part of the Word assessment clients and advisors actually
// read, reproduced deliberately: same two headings, same numbering, same
// wording, same "+Tax+COLA" convention, same red shortfall at the bottom. An
// advisor who has presented it for years should recognise it at a glance, and
// everything newer in this report reads as depth behind a familiar summary
// rather than as a replacement for it.
//
// What is NOT copied is the layout's Word-ness: the tab leaders, the borrowed
// heading bar, the underline-as-subtotal. Those become type, alignment and a
// rule, which is the same information without the 2003 letterhead.

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'];

// Pure's own shorthand, carried verbatim from the Word sheet. The figure is
// what the household needs to spend, before the tax owed on drawing it and
// before the rises inflation will demand. Dropping it understates every
// requirement on the page, so it is never formatted away.
const QUALIFIER = '+Tax+COLA';

function Line({ numeral, label, value, qualifier, variant }) {
  return (
    <div className={`asmt-gs-line${variant ? ` is-${variant}` : ''}`}>
      <span className="asmt-gs-numeral">{numeral}</span>
      <span className="asmt-gs-label">{label}</span>
      <span className="asmt-gs-value">
        {value}
        {qualifier && <span className="asmt-gs-qualifier">{qualifier}</span>}
      </span>
    </div>
  );
}

export default function GoalSheet({ sheet, onPeriod = null }) {
  if (!sheet) return null;
  const { liquid, income } = sheet;

  // The label names the period the figures actually come from. Calling an
  // unplaced household "at retirement" was how a sheet read from this year
  // could be mistaken for a retirement projection.
  const years = `${income.yearsAway} ${income.yearsAway === 1 ? 'year' : 'years'}`;
  const atRetirement = income.period === 'unresolved'
    ? 'Income required today:'
    : income.alreadyThere
      ? 'Income required now that you are retired:'
      : income.period === 'bridge'
        ? `Income required at retirement, before Social Security (${years}):`
        : `Income required at retirement (${years}):`;

  return (
    <section className="asmt-card asmt-goalsheet" id="goal-sheet">
      <div className="asmt-gs-banner">Financial planning goals and concerns</div>

      {/* Which years section 2 reads from, when there is a choice: the bridge
          years before every benefit has started, or the steady state after.
          One of the two things an advisor may change in front of a client;
          the choice is kept with the assessment. A viewer gets no control. */}
      {onPeriod && income.bridge && (
        <div className="asmt-gs-period no-print" role="group" aria-label="Which years section 2 reads from">
          <button
            type="button"
            className={`asmt-gs-period-btn${income.period === 'bridge' ? ' is-on' : ''}`}
            aria-pressed={income.period === 'bridge'}
            onClick={() => onPeriod('bridge')}
          >
            The first years of retirement
          </button>
          <button
            type="button"
            className={`asmt-gs-period-btn${income.period === 'steady' ? ' is-on' : ''}`}
            aria-pressed={income.period === 'steady'}
            onClick={() => onPeriod('steady')}
          >
            Once every benefit has started
          </button>
        </div>
      )}

      <ol className="asmt-gs-outline">
        <li>
          <h3 className="asmt-gs-heading">Preservation and Conservative Growth of Principal</h3>
          <div className="asmt-gs-group">
            <div className="asmt-gs-sub">a. Liquid investments</div>
            <Line numeral="i." label="Tax-Deferred Investments:" value={money(liquid.taxDeferred)} />
            <Line numeral="ii." label="Tax-Free Roth Investments:" value={money(liquid.taxFree)} />
            <Line numeral="iii." label="Taxable Investments:" value={money(liquid.taxable)} />
            <Line numeral="iv." label="Total Liquid Investments:" value={money(liquid.total)} variant="total" />
          </div>
        </li>

        <li>
          <h3 className="asmt-gs-heading">Retirement Income and Cash Flow Strategy</h3>
          <div className="asmt-gs-group">
            <Line
              numeral="a."
              label="Income required today:"
              value={money(income.requiredToday)}
              qualifier={QUALIFIER}
            />
            <Line
              numeral="b."
              label={atRetirement}
              value={money(income.requiredAtRetirement)}
              qualifier={QUALIFIER}
            />
            <div className="asmt-gs-sub is-c">c. Income assumptions:</div>
            {income.sources.map((s, i) => (
              <Line key={s.key} numeral={`${ROMAN[i]}.`} label={`${s.label}:`} value={money(s.amount)} />
            ))}
            <Line
              numeral={`${ROMAN[income.sources.length]}.`}
              label="TOTAL INCOME:"
              value={money(income.totalIncome)}
              variant="total"
            />
            <Line
              numeral={`${ROMAN[income.sources.length + 1]}.`}
              label="Shortfall needed from investments:"
              value={money(income.shortfall)}
              qualifier={QUALIFIER}
              variant="shortfall"
            />
          </div>
        </li>
      </ol>

      {/* The old sheet left the year to be inferred, which is how a shortfall
          could be quoted against income that had not started yet. It also
          described every reading as the settled retirement year, including the
          ones where no such year had been found. */}
      {income.period === 'unresolved' ? (
        <p className="asmt-gs-basis">
          Section 2 is read from {income.atYear}, this year, because the questionnaire
          does not say when
          {income.unresolvedRetirement.length === 1 ? ' this household retires' : ' both people retire'}.
          Add a retirement age or date, or a date of birth to measure the date against,
          and this section will read from the year it belongs to.
        </p>
      ) : income.period === 'bridge' && income.bridge ? (
        <p className="asmt-gs-basis">
          Section 2 is read from {income.atYear}, the first year of retirement, before
          every Social Security payment has started. That gap runs {income.bridge.years}{' '}
          {income.bridge.years === 1 ? 'year' : 'years'}, to {income.bridge.toYear}, and
          costs {money(income.bridge.totalGap)} in total. The shortfall below is one year
          of it, and it is the subtraction of the two figures above it.
        </p>
      ) : !income.alreadyThere ? (
        <p className="asmt-gs-basis">
          Section 2 is read from {income.atYear}, the first year you are both retired and
          every Social Security payment has started. The shortfall is the subtraction of
          the two figures above it, at that one point in time.
        </p>
      ) : null}

      {/* The shorthand is Pure's and clients have seen it for years, but it is
          shorthand, and a packet that goes home on its own has to say what it
          means. Prints with the sheet. */}
      <p className="asmt-gs-footnote">
        {QUALIFIER} means before tax and before inflation: the figure is what it costs to
        live, not what has to be withdrawn to pay for it once tax is taken and prices
        have risen.
      </p>
    </section>
  );
}
